import { init } from '@eppo/js-client-sdk';
import Cookies from 'js-cookie';
import { useCallback, useEffect, useState } from 'react';

import { SEGMENT_ANONYMOUS_ID_COOKIE_NAME } from '~/common/constants/user';
import { getSegmentAnonymousId } from '~/components/analytics/analytics';

import { EppoContext, getOrCreateFallbackSubjectKey } from './helpers';
import { trackEvent } from '../analytics/analytics';
import { EPPO_RANDOMISED_ASSIGNMENT_EVENT_NAME } from '../analytics/constants';
import { useAnalytics } from '../analytics/helpers';

import type { ReactElement } from 'react';

const NEXT_PUBLIC_EPPO_API_KEY = process.env.NEXT_PUBLIC_EPPO_API_KEY as string;
const IS_LHCI_TESTING = process.env.LHCI_TESTING;
const EPPO_ANONYMOUS_ID_TIMEOUT_MS = 4000;
const EPPO_ANONYMOUS_ID_POLL_INTERVAL_MS = 100;

interface IEppoRandomizationProvider {
  children: ReactElement;
}

export const EppoProvider = ({ children }: IEppoRandomizationProvider): ReactElement | null => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isEppoReady, setIsEppoReady] = useState(false);
  const [isError, setIsError] = useState(false);
  const [subjectKey, setSubjectKey] = useState('');
  const { anonymousId } = useAnalytics();

  const getResolvedAnonymousId = useCallback((): string => {
    const cookieAnonymousId = Cookies.get(SEGMENT_ANONYMOUS_ID_COOKIE_NAME);

    return anonymousId || cookieAnonymousId || getSegmentAnonymousId() || '';
  }, [anonymousId]);

  /**
   * Initialize Eppo with the provided API key
   * If the initialization is successful, set isEppoReady to true
   */
  useEffect(() => {
    init({
      apiKey: NEXT_PUBLIC_EPPO_API_KEY,
      assignmentLogger: {
        logAssignment(assignment) {
          trackEvent(EPPO_RANDOMISED_ASSIGNMENT_EVENT_NAME, { ...assignment });
        },
      },
    })
      .then(() => setIsEppoReady(true))
      .catch(() => {
        setIsError(true);
        console.warn('Failed to initialize Eppo');
      });
  }, []);

  /**
   * Prefer Segment's anonymous ID for experiment consistency.
   * If some browsers/ad blockers never expose it, wait briefly for it.
   * Then fall back and keep that fallback stable for the session.
   */
  useEffect(() => {
    if (IS_LHCI_TESTING) {
      setIsInitialized(true);
      return;
    }

    if (!isEppoReady || subjectKey) {
      return;
    }

    const resolvedAnonymousId = getResolvedAnonymousId();
    if (resolvedAnonymousId) {
      setSubjectKey(resolvedAnonymousId);
      setIsInitialized(true);
      return;
    }

    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      const delayedAnonymousId = getResolvedAnonymousId();

      if (delayedAnonymousId) {
        setSubjectKey(delayedAnonymousId);
        setIsInitialized(true);
        window.clearInterval(intervalId);
        return;
      }

      if (Date.now() - startedAt >= EPPO_ANONYMOUS_ID_TIMEOUT_MS) {
        setSubjectKey(getOrCreateFallbackSubjectKey());
        setIsInitialized(true);
        window.clearInterval(intervalId);
      }
    }, EPPO_ANONYMOUS_ID_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [getResolvedAnonymousId, isEppoReady, subjectKey]);

  return (
    <EppoContext.Provider value={{ isInitialized, isError, subjectKey }}>
      {children}
    </EppoContext.Provider>
  );
};
